/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { gnoment } from 'shared-ui/gnoment';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import {
    Box,
    Cell,
    DividerNew as Divider,
    FlexCol,
    FlexRow,
    Row,
    Stack,
    Text,
} from 'sarsaparilla';
import FeeEstimate from './FeeEstimate';
import * as types from '../../constants/index';
import {
    getDayCardinality,
    hasLotteryPreferenceDateDivisionDuration,
    hasLotteryPreferenceDateDivisionGroupSize,
    issuanceStatuses,
    readRuleValue,
} from '../../utils/permitUtilsNew';
import { computeWeekRange } from '../../utils/issuanceUtils';

const propTypes = {
    permit: PropTypes.object.isRequired,
    division: PropTypes.object.isRequired,
    issuance: PropTypes.object.isRequired,
    issuanceList: PropTypes.array,
    permitCount: PropTypes.number,
    entryDate: PropTypes.object,
    exitDate: PropTypes.object,
    groupMembers: PropTypes.arrayOf(PropTypes.object),
    isLottery: PropTypes.bool,
    issuanceStatus: PropTypes.number.isRequired,
    selectedDates: PropTypes.arrayOf(PropTypes.shape({})),
    isPreview: PropTypes.bool,
};

const defaultProps = {
    issuanceStatus: issuanceStatuses.get(types.PERMIT_PRELIMINARY),
    groupMembers: [],
    isPreview: false,
};

export default class ReservationDetail extends React.Component {
    constructor(props) {
        super(props);
        this.onClickAdd = this.onClickAdd.bind(this);
    }

    onClickAdd() {
        this.props.addToCart();
    }

    renderLengthOfStayPreference(preference) {
        const lengthOfStay = preference.length_of_stay
            ? `${preference.length_of_stay} ${getDayCardinality(preference.length_of_stay)}`
            : '';

        return (
            <Row>
                <Cell width={12}>{lengthOfStay}</Cell>
            </Row>
        );
    }

    renderGroupSizePreference(preference) {
        const groupMinSize = preference.group_min_size;
        const groupMaxSize = preference.group_max_size;
        let groupSelection = '';
        // Display group size only if both values have been selected and are valid
        if (groupMinSize && groupMaxSize) {
            groupSelection += `${groupMinSize} - ${groupMaxSize} people`;
        }

        return (
            <Row>
                <Cell width={12}>{groupSelection}</Cell>
            </Row>
        );
    }

    renderMultipleDivisionPreferenceDetail(date, idx) {
        const divisionId = date.division_id;
        const divisionName = this.props.permit.divisions[divisionId]
            ? this.props.permit.divisions[divisionId].name
            : '';
        const selectedDate = date.preferred_date;

        if (gnoment.isGnoment(selectedDate) && selectedDate.isValid()) {
            const key = `${selectedDate.format()}-${idx}`;
            return (
                <div key={key} className="selected-date">
                    <Row>
                        <Cell width={2}>#{idx + 1}.</Cell>
                        <Cell width={10}>
                            {divisionName}
                            <Row>
                                <Cell width={12}>
                                    {selectedDate.format(types.DATE_FORMAT)}
                                </Cell>
                            </Row>
                            {hasLotteryPreferenceDateDivisionDuration(
                                this.props.permit,
                                undefined
                            )
                                ? this.renderLengthOfStayPreference(date)
                                : null}
                            {hasLotteryPreferenceDateDivisionGroupSize(
                                this.props.permit,
                                undefined
                            )
                                ? this.renderGroupSizePreference(date)
                                : null}
                        </Cell>
                    </Row>
                </div>
            );
        }

        return null;
    }

    renderLotteryReservationDatesText() {
        const { selectedDates } = this.props;
        return (
            <div className="selected-dates">
                <p>Lottery Preferences ({selectedDates.length})</p>

                {selectedDates.map((date, i) => {
                    if (date) {
                        if (!isEmpty(date.preferred_date)) {
                            return this.renderMultipleDivisionPreferenceDetail(date, i);
                        }
                    }
                    // otherwise return preference placeholder until further preference data is received
                    return (
                        <div key={i.toString()} className="selected-date">
                            ---
                        </div>
                    );
                })}
            </div>
        );
    }

    renderPermitDateText() {
        const { division, issuanceList } = this.props;
        if (division && division.type === types.DIV_TYPE_DM) {
            if (get(issuanceList, 'length', 0) > 1) {
                return (
                    <Box>
                        <div className="bottom">
                            Mulitple permits within this reservation:
                        </div>
                        {issuanceList.map((issuance, index) => {
                            const { start, end } = computeWeekRange(
                                gnoment(issuance.start_date).utc()
                            );
                            return (
                                <div key={issuance.id}>
                                    <div>{`(${index + 1}) Valid for one day between these dates`}</div>
                                    <b>{`${start.utc().format('LL')} - ${end.utc().format('LL')}`}</b>
                                </div>
                            );
                        })}
                    </Box>
                );
            }
            if (this.props.entryDate) {
                const { start, end } = computeWeekRange(this.props.entryDate);
                return (
                    <Box>
                        <Text size="sm">Valid for one day between these dates</Text>
                        <Text size="sm" fontWeight="bold">
                            {`${start.utc().format('LL')} - ${end.utc().format('LL')}`}
                        </Text>
                    </Box>
                );
            }
            return null;
        }

        let entryDate = '';
        let exitDate = '';
        if (this.props.entryDate) {
            entryDate = this.props.entryDate.utc().format('LL');
        }
        if (this.props.exitDate) {
            exitDate = this.props.exitDate.utc().format('LL');
        }

        if (readRuleValue('ShowEntryDateOnly', this.props.permit)) {
            return (
                <FlexRow>
                    <FlexCol xs={6}>
                        <Text size="sm">Entry Date </Text>
                        <Text size="sm" fontWeight="bold" className="mt-1">
                            {entryDate}
                        </Text>
                    </FlexCol>
                </FlexRow>
            );
        }

        return (
            <FlexRow>
                <FlexCol xs={6}>
                    <Text size="sm">Entry Date </Text>
                    <Text size="sm" fontWeight="bold" className="mt-1">
                        {entryDate}
                    </Text>
                </FlexCol>
                <FlexCol xs={6}>
                    <Text size="sm">Exit Date</Text>
                    <Text size="sm" fontWeight="bold" className="mt-1">
                        {exitDate}
                    </Text>
                </FlexCol>
            </FlexRow>
        );
    }

    render() {
        let type = this.props.division.type;
        if (type === 'OP') {
            type = 'Overnight Paddle';
        }
        if (type === 'OM') {
            type = 'Overnight Motor';
        }
        if (type === 'OH') {
            type = 'Overnight Hike';
        }
        if (type === 'OH') {
            type = 'Overnight Hike';
        }
        if (type === 'DM' || type === 'DC') {
            type = 'Day Use Motor';
        }

        return (
            <Stack>
                <Box>
                    <Text className="mt-0">{type}</Text>
                    {this.props.isLottery
                        ? this.renderLotteryReservationDatesText()
                        : this.renderPermitDateText()}
                </Box>
                <Divider />
                {!this.props.isPreview && (
                    <FeeEstimate
                        permit={this.props.permit}
                        issuance={this.props.issuance}
                        groupMembers={this.props.groupMembers}
                        division={this.props.division}
                        permitCount={this.props.permitCount}
                        fees={this.props.division.fees}
                        issuanceStatus={this.props.issuanceStatus}
                    />
                )}
            </Stack>
        );
    }
}

ReservationDetail.propTypes = propTypes;
ReservationDetail.defaultProps = defaultProps;
